import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useOutletContext } from "react-router";
import { useTranslation } from "react-i18next";
import PatientCard from "../../components/PatientCard";
import { useNavigate } from "react-router-dom";
import "./records.css";
import { ErrorModal } from "../../components/modals/ErrorModal";
import {
  appRatedAtom,
  refreshingAtom,
  isHydrated,
  recordsScrollPositionAtom,
  recordsSelectedIndexAtom,
  consentAcceptedAtomFamily,
  userAtom,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import type { HomeContext } from "../../models/HomeContext";
import { DateTime } from "luxon";
import RefreshButton from "../../components/buttons/RefreshButton";
import { Squirrel } from "../../components/spinner/Squirrel";
import { getAPIClient } from "../../apiClient";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import ReactDOMServer from "react-dom/server";
import useFormFactor from "../../hooks/useFormFactor";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import { InAppReview } from "@capacitor-community/in-app-review";
import { initPullToRefresh } from "../../utils/pull-to-refresh";
import { useBranding } from "../../branding/useBranding";
import MobileFloatingLogo from "../../components/MobileFloatingLogo";
import { DRAWER_ID } from "../home/Home";
import { ImageIcon } from "../../components/ImageIcon";

interface RecordsTranslations {
  searchMessage: string;
  searchButton: string;
  searchError: string;
  header: string;
  subtitle: string;
  updated: string;
}

interface RecordsState {
  searchUid?: string;
}

function indexFromHash(hash: string): number | null {
  const index = parseInt(hash.substring(1));
  return isNaN(index) || index < 0 ? null : index;
}

export function Records({ recordType }: { recordType: "immunization" | "screening" }) {
  const { state } = useLocation();
  const { formFactor } = useFormFactor();
  const { searchUid } = (state as RecordsState) || {};
  const branding = useBranding();
  const { scrollElement, allVerifiedRecords } = useOutletContext<HomeContext>();
  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();
  const [scrollPosition, setScrollPosition] = useAtom(recordsScrollPositionAtom);
  const [selectedRecordIndex, setSelectedRecordIndex] = useAtom(recordsSelectedIndexAtom);
  const [user] = useAtom(userAtom);
  const [consentAccepted] = useAtom(consentAcceptedAtomFamily(user?.email ?? ""));

  const [cardOpen, setCardOpen] = useState<boolean[]>(() => {
    const hash = window.location.hash;
    const hashIndex = indexFromHash(hash);
    // Use hash if available, otherwise fall back to persisted selectedRecordIndex
    const index = hashIndex ?? selectedRecordIndex;
    if (index !== null) {
      const initial = Array(index + 1).fill(false);
      initial[index] = true;
      return initial;
    }
    return [];
  });

  // Sync atom state with URL hash on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const index = indexFromHash(hash);
      if (index !== null && index !== selectedRecordIndex) {
        setSelectedRecordIndex(index);
        setScrollPosition(null);
      }
    } else if (selectedRecordIndex !== null) {
      window.history.replaceState(null, "", `#${selectedRecordIndex}`);
    }
  }, []);

  useEffect(() => {
    // Gives us the fun animation of the first card opening up when no cards are open
    if (cardOpen.length === 0) {
      setSelectedRecordIndex(0);
      setScrollPosition(0);
      setCardOpen([true]);
    }
  }, [cardOpen.length]);
  const [refreshing, setRefreshing] = useAtom(refreshingAtom);
  const [appRated, setAppRated] = useAtom(appRatedAtom);
  const [pullToRefreshMainElement, setPullToRefreshMainElement] = useState<HTMLDivElement | null>(
    null
  );
  const filteredRecords = React.useMemo(() => {
    if (allVerifiedRecords.state !== "hasData" || !isHydrated(allVerifiedRecords.data)) {
      return [];
    }

    if (recordType === "immunization") {
      return allVerifiedRecords.data;
    }

    let filteredRecords = allVerifiedRecords.data;
    /**
     * For now, we only show screenings for New Jersey Searches.
     */
    filteredRecords = filteredRecords.filter(
      (record) => record.search.izProviderKey.toLowerCase() === "nj"
    );

    return filteredRecords;
  }, [allVerifiedRecords, searchUid]);

  useEffect(() => {
    if (!scrollElement) return;
    const handleScroll = () => {
      setScrollPosition(scrollElement.scrollTop);
    };
    scrollElement.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [scrollElement]);

  useEffect(() => {
    if (!scrollElement || !pullToRefreshMainElement) {
      return;
    }
    const ptr = initPullToRefresh({
      pullToRefreshMainElement,
      shouldPullToRefresh: () => {
        // We shouldn't PTR if a dialog is open...
        const dialogOpen = document.querySelector("dialog[open]") !== null;
        const drawerOpen = (document.getElementById(DRAWER_ID) as HTMLInputElement | null)?.checked;
        // iOS bouncing will cause scrollTop to be negative
        return scrollElement.scrollTop <= 0 && !dialogOpen && !drawerOpen;
      },
      onDistance: (p) => {
        // We control the PTR's position on the screen via margin-top
        p.ptrElement.style["marginTop"] = `${p.dist}px`;
        // And we also use the custom --progress property to control the image mask defined in `app.css`
        p.ptrElement.style.setProperty(
          "--progress",
          Math.min(p.dist / p.distThreshold, 1).toString()
        );
        // This is the iOS bounce fix. On iOS the scrollElement can "bounce" when scrolling past the content. In this
        // case the scrollTop can be negative, which causes the PTR to not work as expected since you would end
        // up scrolling content down BUT ALSO adding margin-top to the PTR. This effectively make it feel like
        // you're scrolling ~2x as fast which is super weird. On iOS we just rely on the bounce effect to "pull" the
        // screen down.
        if (scrollElement.scrollTop < 0) {
          p.ptrElement.style["marginTop"] = "0px";
        }
      },
      onRefresh: async () => {
        // Do not await refresh so that it happens in the background without stalling the PTR
        handleRefresh();
      },
    });

    return () => ptr.destroy();
  }, [scrollElement, pullToRefreshMainElement]);

  // Restore scroll position OR scroll to hash (mutually exclusive)
  useEffect(() => {
    if (!scrollElement || filteredRecords.length === 0) {
      return;
    }

    const index = indexFromHash(window.location.hash);

    // If we have a saved position (back navigation), restore it
    if (scrollPosition !== null && (index === null || index === selectedRecordIndex)) {
      requestAnimationFrame(() => {
        scrollElement.scrollTo({ top: scrollPosition, behavior: "instant" });
      });
      return;
    }

    // Otherwise, if there's a hash, scroll to that card
    if (index !== null && index < filteredRecords.length) {
      const element = document.querySelector(`[data-card-index="${index}"]`);
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [filteredRecords.length, scrollElement]); // scrollPosition intentionally omitted

  const rateTheApp = async () => {
    if (Capacitor.getPlatform() === "web") return;
    await setAppRated(true);
    /**
     * From the Apple docs: Because this method may not present an alert, don’t call requestReview() or
     * requestReview(in:) in response to a button tap or other user action.
     *
     * That means we cannot first present a dialog asking the user to rate the app since they might press "yes" and then
     * not see any review dialog pop up.
     */
    await InAppReview.requestReview();
  };

  useEffect(() => {
    // Do not want to refresh infinitely if reloading in the middle of a refresh
    setRefreshing(false);
  }, []);

  useEffect(() => {
    if (filteredRecords.length > 0 && !appRated && consentAccepted) {
      rateTheApp();
    }
  }, [JSON.stringify(filteredRecords.map((r) => r.record.uid)), appRated, consentAccepted]);

  // Only 1 card can be open at a time
  const toggleCardOpen = (index: number) => {
    const prevState = cardOpen[index];
    // Reset the state and set scroll atoms to include the new card and current scroll position
    window.history.replaceState(null, "", `#${index}`);
    setSelectedRecordIndex(prevState ? null : index);
    setScrollPosition(scrollElement?.scrollTop ?? null);
    const newOpen = Array(filteredRecords.length).fill(false);
    newOpen[index] = !prevState;
    setCardOpen(newOpen);
  };

  const navigate = useNavigate();
  const { t } = useTranslation();

  const translation = useMemo<RecordsTranslations>(() => {
    return {
      searchMessage:
        recordType === "immunization"
          ? t("immunizations.search_records_message")
          : t("screening.search_screenings_message"),
      searchButton:
        recordType === "immunization"
          ? t("immunizations.immunizations_add_records_button")
          : t("screening.search_screenings_button"),
      searchError:
        recordType === "immunization"
          ? t("immunizations.search_records_error")
          : t("screening.search_records_error"),
      header:
        recordType === "immunization"
          ? t("immunizations.records_header")
          : t("screening.records_header"),
      subtitle:
        recordType === "immunization"
          ? formFactor === "mobile"
            ? t("immunizations.records_subtitle_mobile")
            : t("immunizations.records_subtitle")
          : formFactor === "mobile"
          ? t("screening.records_subtitle_mobile")
          : t("screening.records_subtitle"),
      updated: t("immunizations.updated"),
    };
  }, [recordType, formFactor]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await getAPIClient().refreshRecords();
    /**
     * Do not await this so that when we refresh we don't show a loading squirrel for the entire time it takes for the
     * backend to refresh all records from the IIS.
     */
    fetchRecordsUntilUpdated(true);
    // Give the sense of loading doing something a bit more than firing off an API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const noPatients = () => {
    return (
      <div className="flex px-6 flex-col gap-2" data-testid="no-patients">
        <div role="alert" className="alert">
          <div className="flex max-md:flex-col items-center gap-2">
            <ImageIcon src="/assets/images/Icon_Alert.png" className="text-primary w-7 h-7" />
            {translation.searchMessage}
          </div>
        </div>

        <button
          className="btn btn-primary md:w-fit max-md:w-full mx-auto"
          onClick={() => navigate("/home/search")}
        >
          {translation.searchButton}
        </button>
      </div>
    );
  };

  const withPatients = () => {
    if (allVerifiedRecords.state !== "hasData" || !isHydrated(allVerifiedRecords.data)) {
      return null;
    }
    const lastUpdatedTime: Date | null = allVerifiedRecords.data.reduce<Date | null>(
      (maxUpdatedDate, cur) => {
        if (!cur.record.updatedAt) {
          return maxUpdatedDate || null;
        }

        const currentUpdatedDate = new Date(cur.record.updatedAt);
        if (isNaN(currentUpdatedDate.getTime())) {
          return maxUpdatedDate;
        }

        return maxUpdatedDate === null || maxUpdatedDate < currentUpdatedDate
          ? currentUpdatedDate
          : maxUpdatedDate;
      },
      null
    );

    const patientCards = (
      <div className="flex flex-col gap-2">
        {filteredRecords.map((r, index) => (
          <div data-card-index={index} key={index}>
            <PatientCard
              completeRecord={r}
              index={index}
              open={cardOpen[index] ?? false}
              onOpenToggle={() => toggleCardOpen(index)}
              screening={recordType === "screening"}
            />
          </div>
        ))}
      </div>
    );
    const headerContent = (
      <div className="flex flex-col md:flex-row w-full justify-center md:justify-between not-jersey:text-primary-content jersey:max-md:text-primary-content items-center pb-4 md:pb-6 ">
        <p className="text-3xl font-bold">{translation.header}</p>
        {lastUpdatedTime && (
          <p className="md:hidden font-semibold text-primary">
            {translation.updated}
            <span data-testid="update-time">
              {DateTime.fromJSDate(lastUpdatedTime).toLocaleString()}
            </span>
          </p>
        )}
        <div className="flex items-center gap-2 ml-auto max-md:hidden">
          {lastUpdatedTime && (
            <p className="md:text-base text-right">
              {translation.updated}
              <span data-testid="update-time">
                {DateTime.fromJSDate(lastUpdatedTime).toLocaleString()}
              </span>
            </p>
          )}
          <RefreshButton />
        </div>
      </div>
    );

    return (
      <>
        {headerContent}
        <div data-testid="records-list" className="max-md:flex max-md:flex-col">
          {patientCards}
        </div>
      </>
    );
  };

  if (allVerifiedRecords.state === "hasError") {
    setShowErrorModal(translation.searchError);
  }

  return (
    <>
      <div className="-mt-20 md:hidden pb-4">
        <div className="" id="pull-to-refresh-main-records" ref={setPullToRefreshMainElement}></div>
      </div>
      <MobileFloatingLogo />
      <div className="flex-col max-md:pb-20 max-md:pt-6 override-bg-to-neutral">
        {showErrorModal && (
          <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
        )}
        {(allVerifiedRecords.state === "loading" ||
          refreshing ||
          (allVerifiedRecords.state === "hasData" && !isHydrated(allVerifiedRecords.data))) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <Squirrel />
          </div>
        )}
        <div className="column is-gapless">
          {allVerifiedRecords.state === "hasData" &&
            isHydrated(allVerifiedRecords.data) &&
            allVerifiedRecords.data.length > 0 &&
            withPatients()}
          {allVerifiedRecords.state === "hasData" &&
            isHydrated(allVerifiedRecords.data) &&
            allVerifiedRecords.data.length === 0 &&
            noPatients()}
        </div>
      </div>
    </>
  );
}

export default Records;
